'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const relatedServices = [
  { label: 'Container Transport', href: '/container-transport' },
  { label: 'Less Container Load (LCL)', href: '/lcl-transport' },
  { label: 'Airfreight Transport', href: '/airfreight-transport' },
  { label: 'Inland Transport', href: '/inland-transport' },
  { label: 'Port Operations', href: '/port-operations' },
  { label: 'Cargo Insurance', href: '/cargo-insurance' },
  { label: 'Bank Guarantee', href: '/bank-guarantee' },
];

export default function CustomsRepresentationPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero section */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm lg:max-w-md" aria-label="Customs clearance document illustration">
                <rect x="80" y="40" width="160" height="200" rx="10" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.04)"/>
                <path d="M200 40 L240 80 L200 80 Z" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.1)"/>
                <path d="M200 40 L200 80 L240 80" stroke="#1B6B8A" strokeWidth="3" fill="none"/>
                <line x1="100" y1="110" x2="220" y2="110" stroke="#2A8FAB" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="100" y1="130" x2="220" y2="130" stroke="#2A8FAB" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="100" y1="150" x2="180" y2="150" stroke="#2A8FAB" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="100" y1="170" x2="220" y2="170" stroke="#7EC8DC" strokeWidth="2" strokeLinecap="round"/>
                <line x1="100" y1="188" x2="200" y2="188" stroke="#7EC8DC" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="190" cy="195" r="28" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.06)" strokeDasharray="5 3"/>
                <circle cx="190" cy="195" r="20" stroke="#1B6B8A" strokeWidth="2" fill="none"/>
                <path d="M180 195 L187 202 L202 186" stroke="#1B6B8A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="68" cy="200" r="22" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.05)"/>
                <line x1="84" y1="216" x2="100" y2="232" stroke="#1B6B8A" strokeWidth="4" strokeLinecap="round"/>
                <line x1="58" y1="196" x2="78" y2="196" stroke="#2A8FAB" strokeWidth="2" strokeLinecap="round"/>
                <line x1="68" y1="186" x2="68" y2="206" stroke="#2A8FAB" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Text */}
            <div>
              <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
                Customs representation
              </h1>
              <div className="space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: '#3A5070' }}>
                <p>World Transport Overseas provides expert <strong style={{ color: '#0F2340' }}>customs representation services</strong> for importers and exporters navigating the complexities of international trade regulations. Our licensed customs brokers have deep expertise in EU and international customs procedures, ensuring your goods clear customs efficiently and in full compliance.</p>
                <p>We handle all aspects of customs clearance — from tariff classification and duty calculation to the preparation and submission of import and export declarations. Our team stays up to date with the latest regulatory changes, ensuring your shipments are always compliant with current requirements.</p>
                <p>Beyond standard clearance, WTO offers strategic customs consultancy including duty optimisation, preferential tariff advice, and support for Authorised Economic Operator (AEO) status applications. Our goal is to minimise your customs costs while ensuring full regulatory compliance across all transport modes.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link href="/transport-quote" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-md" style={{ background: '#1A5FAB' }}>
                  Get a Quote
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key highlights */}
        <section className="py-14" style={{ background: '#0F2340' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Our Customs Expertise</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Licensed Brokers', desc: 'Certified customs agents with deep expertise in EU and international trade regulations' },
                { title: 'All Trade Modes', desc: 'Import and export declarations for sea, air, road, and rail shipments' },
                { title: 'Tariff Classification', desc: 'Accurate HS code classification to ensure correct duty rates and compliance' },
                { title: 'Duty Optimisation', desc: 'Expert advice on preferential tariffs, duty relief schemes, and trade agreements' },
              ]?.map((h, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="w-8 h-8 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'rgba(126,200,220,0.2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#7EC8DC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.21.45 4.55 1.23" /></svg>
                  </div>
                  <p className="font-bold text-base mb-1" style={{ color: '#FFFFFF' }}>{h?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{h?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services list */}
        <section className="py-14" style={{ background: '#0A1628' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Customs Services</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: 'Import Customs Clearance', desc: 'Full handling of import declarations, duty payment, and release of goods from customs control.' },
                { title: 'Export Customs Clearance', desc: 'Preparation and submission of export declarations with all required supporting documentation.' },
                { title: 'Transit Procedures', desc: 'Management of T1/T2 transit documents for goods moving through multiple customs territories.' },
                { title: 'Tariff Classification', desc: 'Expert HS code determination to ensure correct duty rates and avoid costly misclassification penalties.' },
                { title: 'Origin Determination', desc: 'Assessment of preferential and non-preferential origin for duty optimisation under trade agreements.' },
                { title: 'AEO Consultancy', desc: 'Support for Authorised Economic Operator status applications and compliance audits.' },
              ]?.map((c, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="font-bold text-base mb-2" style={{ color: '#FFFFFF' }}>{c?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{c?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14" style={{ background: '#0E1F38' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Other Services</h2>
            <div className="flex flex-wrap gap-3">
              {relatedServices?.map((s) => (
                <Link key={s?.href} href={s?.href} className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80" style={{ background: '#132848', color: '#B8CDE0', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
